import { Fragment, type MouseEvent, type ReactElement, useState } from "react";
import {
  Bar,
  BarChart,
  LabelList,
  Legend as RechartsLegend,
  Rectangle,
  type RectangleProps,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Text,
  XAxis,
  YAxis,
} from "recharts";
import { Legend } from "src/components/analytics/shared/legend";
import { chartBaseColors } from "src/pages/analytics/constants";
import {
  FunnelMeasurementType,
  type FunnelStepGraphData,
  type HoveredSection,
} from "src/pages/analytics/types";
import { noop } from "ts-essentials";
import { Label } from "./label";
import CustomTooltip from "./tooltip";
import {
  FunnelChartLoader,
  ChartWithLoaderText,
} from "src/components/charts/loaders";
import {
  getYAxisStyles,
  getXAxisStyles,
  getColumnRadius,
  chartFontStyles,
} from "src/components/charts/chart-utils";
import { renderCartesianGrid } from "src/components/charts/chart-grid";
import slugify from "@sindresorhus/slugify";

// Funnels need to start at index 3 of `chartBaseColors` so that the first color is green.
const funnelsStartingIndex = 3;

// Vertical space reserved for x-axis ticks. Tall enough to fit two lines of
// wrapped step labels without clipping.
const X_AXIS_HEIGHT = 48;
// Padding subtracted from each tick's slot so adjacent wrapped labels don't
// touch.
const X_AXIS_TICK_GAP = 8;

/**
 * Renders a funnel step label using Recharts' `<Text>` SVG component, which
 * wraps on word boundaries based on real canvas glyph measurement of the
 * configured font. We hand it the per-bar slot width derived from the axis's
 * own `width` / `visibleTicksCount` so wrapping adapts to chart size.
 */
const buildFunnelTickRenderer = (
  graphData: FunnelStepGraphData[],
  showStepIndices: boolean,
) => {
  const renderFunnelTick = (props: {
    x?: number;
    y?: number;
    payload?: { value: number };
    width?: number;
    visibleTicksCount?: number;
  }) => {
    const { x, y, payload, width, visibleTicksCount } = props;
    if (!payload) return <g />;

    const index = payload.value;
    const stageName = graphData[index]?.stageName ?? "";
    const label = showStepIndices ? `${index + 1}. ${stageName}` : stageName;

    const slotWidth =
      width && visibleTicksCount && visibleTicksCount > 0
        ? Math.max(0, width / visibleTicksCount - X_AXIS_TICK_GAP)
        : undefined;

    return (
      <Text
        x={x}
        y={y}
        width={slotWidth}
        textAnchor="middle"
        verticalAnchor="start"
        style={chartFontStyles}
      >
        {label}
      </Text>
    );
  };
  return renderFunnelTick;
};

export type ClickFunnelBarHandler = (args: {
  payload: FunnelStepGraphData;
  event: MouseEvent;
  seriesName: string;
  didPerform?: boolean;
}) => void;

export const FunnelGraph = ({
  isLoading = false,
  showLoadingText = true,
  graphData,
  showLegend = false,
  height,
  onClickBar,
  disableAnimation = false,
  showStepIndices = true,
  barBackground,
  renderTooltip,
}: {
  isLoading?: boolean;
  showLoadingText?: boolean;
  graphData: FunnelStepGraphData[];
  showLegend?: boolean;
  height?: string;
  onClickBar?: ClickFunnelBarHandler;
  disableAnimation?: boolean;
  showStepIndices?: boolean;
  barBackground?: string;
  renderTooltip?: (hoveredSection: HoveredSection) => ReactElement;
}) => {
  const isAnimationActive = !disableAnimation;
  const [hoveredSection, setHoveredSection] = useState<HoveredSection>({
    dataKey: null,
    seriesName: null,
    section: "conversion",
    index: undefined,
  });

  if (isLoading) {
    return showLoadingText ? (
      <ChartWithLoaderText boxSize="100%">
        <FunnelChartLoader mb={6} />
      </ChartWithLoaderText>
    ) : (
      <FunnelChartLoader mb={6} />
    );
  }

  const tooltipContent = renderTooltip ? (
    renderTooltip(hoveredSection)
  ) : (
    <CustomTooltip
      hoveredSection={hoveredSection}
      funnelMeasuringType={FunnelMeasurementType.TotalConversion}
    />
  );

  return (
    <ResponsiveContainer minHeight={height}>
      <BarChart
        data={graphData}
        margin={{
          top: showLegend ? 0 : 20, // Top margin required to account for label position when no legend is present
          right: -10, // Offset y-axis labels
          left: 0,
        }}
        onMouseLeave={() =>
          setHoveredSection({
            dataKey: null,
            seriesName: null,
            section: "conversion",
            index: undefined,
          })
        }
      >
        {renderCartesianGrid()}
        <XAxis
          dataKey="index"
          interval={0}
          minTickGap={50}
          {...getXAxisStyles({ tickAlign: "center" })}
          height={X_AXIS_HEIGHT}
          tick={buildFunnelTickRenderer(graphData, showStepIndices)}
        />
        <YAxis
          {...getYAxisStyles({ allowDecimals: true })}
          tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
          ticks={[0, 0.25, 0.5, 0.75, 1]}
        />
        <RechartsTooltip
          animationDuration={200}
          animationEasing="ease"
          content={tooltipContent}
          cursor={{ fill: "transparent" }}
          wrapperStyle={{ outline: "none" }}
        />
        {graphData.length > 0 &&
          Object.keys(graphData[0]!.data).map((seriesName, index) => {
            const colorIndex = index + funnelsStartingIndex;
            const baseColor =
              chartBaseColors[colorIndex % chartBaseColors.length]!.color;
            const lightColor =
              chartBaseColors[colorIndex % chartBaseColors.length]!.bg;

            const seriesNameToId = slugify(seriesName);

            const columnRadius = getColumnRadius(graphData.length);
            const barStyle = {
              transition: "opacity 120ms ease-out",
              cursor: onClickBar ? "pointer" : "default",
            };

            // Apply the hover dim in the bar `shape` rather than via per-`Cell`
            // `opacity`. Cell opacity is part of the bar's composed data, which
            // Recharts deep-compares to decide whether to replay the
            // grow-from-zero entry animation — so dimming through a Cell makes
            // every hover re-animate the bars (a flicker). Reading
            // `hoveredSection` in the shape keeps the composed data stable, so
            // the entry animation plays once and hover stays steady.
            const isHovered = (
              section: HoveredSection["section"],
              i?: number,
            ) =>
              hoveredSection.seriesName === seriesName &&
              hoveredSection.section === section &&
              hoveredSection.index === i;

            // Optional faint full-height track behind the column. Kept visually
            // distinct from the (colored) drop-off ghost.
            const renderTrack = (props: unknown) => {
              const rectProps = props as RectangleProps;
              return (
                <Rectangle
                  {...rectProps}
                  fill={barBackground}
                  radius={columnRadius}
                />
              );
            };

            // Round the solid bar's top only when it fills the whole column
            // (e.g. a 100% first step). A partially filled bar keeps a flat fill
            // line, and its rounded top is instead supplied by the drop-off
            // ghost stacked on top.
            const renderConversionBar = (props: unknown) => {
              const {
                payload,
                index: i,
                ...rest
              } = props as RectangleProps & {
                payload?: FunnelStepGraphData;
                index?: number;
              };
              const size = payload?.data?.[seriesName]?.conversionBarSize ?? 0;
              return (
                <Rectangle
                  {...rest}
                  radius={size >= 1 ? columnRadius : 0}
                  opacity={isHovered("conversion", i) ? 0.7 : 1}
                  style={barStyle}
                />
              );
            };

            const renderDropOffBar = (props: unknown) => {
              const { index: i, ...rest } = props as RectangleProps & {
                index?: number;
              };
              return (
                <Rectangle
                  {...rest}
                  radius={columnRadius}
                  opacity={isHovered("dropOff", i) ? 1 : 0.7}
                  style={barStyle}
                />
              );
            };

            return (
              <Fragment key={seriesNameToId}>
                <Bar
                  dataKey={`data.${seriesName}.conversionBarSize`}
                  stackId={seriesName}
                  fill={baseColor}
                  isAnimationActive={isAnimationActive}
                  shape={renderConversionBar}
                  {...(barBackground ? { background: renderTrack } : {})}
                  onClick={
                    onClickBar
                      ? (data, _, event) => {
                          onClickBar({
                            payload: data.payload,
                            event,
                            seriesName,
                          });
                        }
                      : undefined
                  }
                  onMouseEnter={(_, i) => {
                    if (
                      hoveredSection.seriesName !== seriesName ||
                      hoveredSection.section !== "conversion" ||
                      hoveredSection.index !== i
                    ) {
                      setHoveredSection({
                        dataKey: `data.${seriesName}.conversionBarSize`,
                        seriesName,
                        section: "conversion",
                        index: i,
                      });
                    }
                  }}
                />
                <defs>
                  <linearGradient
                    id={`${seriesNameToId}-gradient`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={lightColor} stopOpacity={1} />
                    <stop
                      offset="200%"
                      stopColor={lightColor}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <Bar
                  dataKey={`data.${seriesName}.dropOffBarSize`}
                  stackId={seriesName}
                  fill={`url(#${seriesNameToId}-gradient)`}
                  isAnimationActive={isAnimationActive}
                  shape={renderDropOffBar}
                  onClick={
                    onClickBar
                      ? (data, _, event) =>
                          onClickBar({
                            payload: data.payload,
                            event,
                            seriesName,
                            didPerform: false,
                          })
                      : undefined
                  }
                  onMouseEnter={(_, i) => {
                    if (
                      hoveredSection.seriesName !== seriesName ||
                      hoveredSection.section !== "dropOff" ||
                      hoveredSection.index !== i
                    ) {
                      setHoveredSection({
                        dataKey: `data.${seriesName}.dropOffBarSize`,
                        seriesName,
                        section: "dropOff",
                        index: i,
                      });
                    }
                  }}
                >
                  <LabelList
                    dataKey={`data.${seriesName}.conversion`}
                    formatter={(value: number) => `${value}%`}
                    position="bottom"
                    content={<Label />}
                  />
                </Bar>
              </Fragment>
            );
          })}
        {showLegend && (
          <RechartsLegend
            verticalAlign="top"
            content={() => (
              <Legend
                onHover={noop}
                series={Object.keys(graphData?.[0]?.data ?? []).map(
                  (seriesName, index) => ({
                    key: seriesName,
                    legendName: seriesName,
                    color:
                      chartBaseColors[
                        (index + funnelsStartingIndex) % chartBaseColors.length
                      ]!.color,
                  }),
                )}
              />
            )}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};
